import { useLocation } from 'react-router-dom';
import { Text } from 'foundations-components/components/Text';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { getIsIframePreviewUrl, getIsPaymentPurchaseUrl } from '../../utils/urls';
import { getIsAnyRecurring } from '../../utils/recurring';
import { useTransactionLimits } from '../../hooks/useTransactionLimits';
import RecurringTotalsMessage from '../RecurringTotalsMessage';
import { useValidationErrors } from '../../hooks/useValidationErrors';
import { MAX_RECURRING_SUBTOTAL_EXCEEDED, MAX_TOTAL_EXCEEDED, MIN_RECURRING_SUBTOTAL_NOT_REACHED, MIN_TOTAL_NOT_REACHED } from '../../constants/ValidationErrors';
import { hasCustomConfiguration, isOptional } from '../../types/utils/typeguards';
import { useDispatch, useSelector } from 'react-redux';
import { getLineItemBlurs } from '../../state/lineItemBlursSlice';
import { useEffect } from 'react';
import { getShowAllErrors, setErrorMessages } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TotalsValidationMessages = ({
  checkoutSession,
  selectedOptionalLineItems
}) => {
  const dispatch = useDispatch();
  const showAllErrors = useSelector(getShowAllErrors);
  const lineItemBlurs = useSelector(getLineItemBlurs);
  const {
    pathname
  } = useLocation();
  const shouldShowRecurringMessage = (getIsPaymentPurchaseUrl(pathname) || getIsIframePreviewUrl(pathname)) && getIsAnyRecurring(checkoutSession.lineItems, selectedOptionalLineItems) && checkoutSession.lineItems.every(lineItem => lineItem.billedAtCheckout);
  const shouldShowAtCheckoutRecurringMessage = showAllErrors || checkoutSession.lineItems.some(lineItem => lineItem.billedAtCheckout && !!lineItem.recurringBillingTerms && lineItemBlurs[lineItem.id]);
  const shouldShowAtCheckoutNonRecurringMessage = showAllErrors || checkoutSession.lineItems.some(lineItem => lineItem.billedAtCheckout && !lineItem.recurringBillingTerms && lineItemBlurs[lineItem.id]);
  const areAllItemsOptional = checkoutSession.lineItems.every(l => hasCustomConfiguration(l) && isOptional(l));
  const {
    subtotalsValidationErrors
  } = useValidationErrors();
  const filteredSubtotalValidationErrors = subtotalsValidationErrors.filter(error => {
    var _error$context;
    return !((_error$context = error.context) !== null && _error$context !== void 0 && (_error$context = _error$context.initialPaymentType) !== null && _error$context !== void 0 && _error$context.includes('FUTURE_START_DATE'));
  });
  const isOrderTotalAboveMax = shouldShowAtCheckoutNonRecurringMessage && filteredSubtotalValidationErrors.some(err => {
    return err.subCategory === MAX_TOTAL_EXCEEDED;
  });
  const isOrderTotalBelowMin = shouldShowAtCheckoutNonRecurringMessage && filteredSubtotalValidationErrors.some(err => {
    return err.subCategory === MIN_TOTAL_NOT_REACHED;
  });
  const isRecurringTotalBelowMin = shouldShowAtCheckoutRecurringMessage && filteredSubtotalValidationErrors.some(err => {
    return err.subCategory === MIN_RECURRING_SUBTOTAL_NOT_REACHED;
  });
  const isRecurringTotalAboveMax = shouldShowAtCheckoutRecurringMessage && filteredSubtotalValidationErrors.some(err => {
    return err.subCategory === MAX_RECURRING_SUBTOTAL_EXCEEDED;
  });
  const areAllOptionalLineItemsUnselected = Boolean(areAllItemsOptional && (selectedOptionalLineItems === null || selectedOptionalLineItems === void 0 ? void 0 : selectedOptionalLineItems.every(isSelected => isSelected === false)));
  const {
    limits
  } = useTransactionLimits();
  const currencyCode = checkoutSession.currencyCode;
  const {
    formState
  } = usePaymentFormContext();
  const paymentMethod = formState.get('paymentMethod');
  const minimumAmount = limits && limits.minimumAmountForPaymentMethod[paymentMethod];
  const maximumAmount = limits && limits.maximumAmountForPaymentMethod[paymentMethod];
  useEffect(() => {
    dispatch(setErrorMessages({
      totalOverMax: {
        isError: isOrderTotalAboveMax || isRecurringTotalAboveMax,
        totalsOptions: {
          currencyCode,
          maximumAmount
        }
      },
      totalBelowMin: {
        isError: isOrderTotalBelowMin,
        totalsOptions: {
          currencyCode,
          minimumAmount
        }
      },
      recurringDueNowTotalBelowMin: {
        isError: !isOrderTotalBelowMin && isRecurringTotalBelowMin,
        totalsOptions: {
          currencyCode,
          minimumAmount
        }
      },
      allOptionalItemsUnselected: {
        isError: areAllOptionalLineItemsUnselected
      }
    }));
  }, [areAllOptionalLineItemsUnselected, currencyCode, dispatch, isOrderTotalAboveMax, isOrderTotalBelowMin, isRecurringTotalAboveMax, isRecurringTotalBelowMin, minimumAmount, maximumAmount]);
  const allOptionalItemsUnselectedMessage = useGetErrorMessagesAndTranslations().get('allOptionalItemsUnselected');
  const belowMinMessage = useGetErrorMessagesAndTranslations().get(!isOrderTotalBelowMin && isRecurringTotalBelowMin ? 'recurringTotalBelowMin' : 'totalBelowMin');
  const totalOverMaxMessage = useGetErrorMessagesAndTranslations().get('totalOverMax');
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [areAllOptionalLineItemsUnselected && showAllErrors && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      textAlign: "end",
      "data-test-id": "minimum-validation-optional-error-message",
      as: "p",
      typography: "detail-100",
      color: "text-alert-default",
      children: allOptionalItemsUnselectedMessage
    }), (isOrderTotalBelowMin || isRecurringTotalBelowMin) && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      "data-test-id": "minimum-validation-error-message",
      as: "p",
      typography: "detail-100",
      textAlign: "end",
      color: "text-alert-default",
      children: belowMinMessage
    }), (isOrderTotalAboveMax || isRecurringTotalAboveMax) && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      "data-test-id": "maximum-validation-error-message",
      as: "p",
      typography: "detail-100",
      textAlign: "end",
      color: "text-alert-default",
      children: totalOverMaxMessage
    }), shouldShowRecurringMessage && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      as: "p",
      typography: "detail-100",
      color: "text-core-subtle",
      "data-test-id": "subsequent-payment-message",
      textAlign: "end",
      children: /*#__PURE__*/_jsx(RecurringTotalsMessage, {
        currencyCode: currencyCode
      })
    })]
  });
};
export default TotalsValidationMessages;